import { useCallback, useEffect, useMemo } from 'react';
import Cookies from 'js-cookie';
import { BuilderAddResult, Cart } from 'shared/types/cart';
import { ShippingAddress, ShippingMethod } from 'shared/types/cart/CustomShippingMethod';
import { Discount } from 'shared/types/cart/Discount';
import { ExtendedLineItem } from 'shared/types/cart/LineItem';
import { Order } from 'shared/types/cart/Order';
import { Variant } from 'shared/types/product';
import useSWR, { mutate } from 'swr';
import { ITEMS_IN_CART } from 'helpers/constants/localStorage';
import useI18n from 'helpers/hooks/useI18n';
import mapCosts from 'helpers/utils/mapCosts';
import { sdk } from 'sdk';
import { AddCartItemPayload } from 'sdk/composable-commerce/types/payloads/CartPayloads';
import { revalidateOptions } from 'frontastic';
import { publishCart, readAuthoritativeCart } from './publishCart';
import { CartDetails, SavedCartDetails, SavedCartResult, UseCartReturn } from './types';

const useCart = (props: { shippingMethod?: ShippingMethod } = {}): UseCartReturn => {
  const { shippingMethod = undefined } = props;
  const extensions = sdk.composableCommerce;

  const { currency } = useI18n();

  // Every read of the session cart (first load and every revalidation) is authoritative for WHICH
  // cart the client is on; publishCart drops snapshots of any other cart.
  const result = useSWR('/action/cart/getCart', () => readAuthoritativeCart(() => extensions.cart.getCart()), {
    ...revalidateOptions,
    revalidateIfStale: true,
  });

  const data = result.data?.isError ? {} : { data: result.data?.data as unknown as Cart };

  useEffect(() => {
    Cookies.set(ITEMS_IN_CART, JSON.stringify(!!data.data?.lineItems?.length));
  }, [data.data?.lineItems]);

  const estimateShipping = async (address: ShippingAddress) => {
    if (!address.zipcode) return;
    const shippingMethods = await extensions.cart.getCustomShippingMethods({
      address,
      items: data.data?.lineItems ?? [],
    });
    if (shippingMethods.isError)
      throw new Error(shippingMethods.error.message ?? shippingMethods.error?.response?.errors?.[0]?.message ?? '');
    return shippingMethods.data;
  };

  const totalItems = (data.data as Cart)?.lineItems?.reduce((acc, curr) => acc + (curr.count as number), 0) ?? 0;

  const isEmpty = !data?.data?.lineItems?.length;

  const isShippingAccurate = !!data?.data?.shippingInfo;

  const billingAddress = data?.data?.billingAddress;
  const shippingAddress = data?.data?.shippingAddress;

  const transaction = useMemo(
    () => mapCosts({ cart: data.data, currency, shippingMethod }),
    [data.data, currency, shippingMethod],
  );

  const addItem = useCallback(
    async (variant: Variant, quantity: number, finish?: string, sheen?: string, hasWirebrush?: boolean) => {
      try {
        const extensions = sdk.composableCommerce;
        const payload: AddCartItemPayload = {
          variant: {
            sku: variant.sku,
            count: quantity,
          },
        };

        if (finish) payload.variant.finish = finish;
        if (sheen) payload.variant.sheen = sheen;
        if (hasWirebrush) payload.variant.hasWirebrush = hasWirebrush;

        const res = await extensions.cart.addItem(payload);

        if (res.isError) {
          console.error('Error adding item to cart:', res.error);
          throw new Error(res.error?.message || 'Error adding item to cart');
        }

        publishCart(res);

        return res.data as Cart;
      } catch (error) {
        console.error('Unexpected error adding item to cart:', error);
        throw error;
      }
    },
    [],
  );

  const addBuilderItems = useCallback(
    async (designId: string, options?: { isCurrent?: () => boolean; discountCode?: string }): Promise<BuilderAddResult> => {
      try {
        const res = await extensions.cart.addBuilderItems({
          designId,
          builderResult: 2,
          ...(options?.discountCode ? { discountCode: options.discountCode } : {}),
        });

        if (res.isError) {
          console.error('Error adding builder items to cart:', res.error);
          throw new Error(res.error?.message || 'Error adding builder items');
        }

        // single SWR update, skipped for a superseded request so it cannot publish an older cart.
        // A superseded request may still have CHANGED the server cart (it cannot be un-sent), so
        // its response is not trusted either way: re-read the true server cart instead.
        const result = res.data as BuilderAddResult;
        if (!(options?.isCurrent?.() ?? true)) {
          mutate('/action/cart/getCart');
        } else if (result.kind === 'cart') {
          // Only a cart result carries a snapshot; a 'no-cart' reply leaves the cache as it is.
          publishCart({ ...res, data: result.cart });
        }

        return result;
      } catch (error) {
        console.error('Unexpected error adding builder items:', error);
        throw error;
      }
    },
    [],
  );

  // RoleModel/DPQ add-to-cart: the caller has already resolved a project key into
  // concrete SKUs + quantities + finishes (via getDPQProducts) and passes them here.
  const addDPQItems = useCallback(
    async (
      items: {
        variant: Variant;
        quantity: number;
        finish?: string;
        sheen?: string;
        hasWirebrush?: boolean;
      }[],
      projectKey?: string,
      options?: { isCurrent?: () => boolean },
    ): Promise<Cart> => {
      try {
        const payload = {
          items: items.map((item) => ({
            variant: {
              sku: item.variant.sku,
              count: item.quantity,
              ...(item.finish ? { finish: item.finish } : {}),
              ...(item.sheen ? { sheen: item.sheen } : {}),
              ...(item.hasWirebrush ? { hasWirebrush: item.hasWirebrush } : {}),
            },
          })),
          ...(projectKey ? { projectKey } : {}),
        };

        const res = await extensions.cart.addDPQItems(payload);

        if (res.isError) {
          console.error('Error adding multiple items to cart:', res.error);
          throw new Error(res.error?.message || 'Error adding multiple items');
        }

        // Checked BEFORE publishing: a superseded request publishes nothing. It may still have
        // changed the server cart (it cannot be un-sent), so the true cart is re-read instead.
        if (options?.isCurrent?.() ?? true) {
          publishCart(res);
        } else {
          mutate('/action/cart/getCart');
        }

        return res.data as Cart;
      } catch (error) {
        console.error('Unexpected error adding multiple items:', error);
        throw error;
      }
    },
    [],
  );

  const orderCart = useCallback(async (options?: { requireZeroTotal?: boolean }) => {
    const res = await sdk.callAction({ actionName: 'cart/checkout', payload: options });
    mutate('/action/cart/getCart');

    if (res.isError) {
      throw new Error(res.error.message || res.error.response?.errors?.[0]?.message || 'Unable to place your order.');
    }

    return res.data as Order;
  }, []);

  const queryOrder = useCallback(async (orderId: string) => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.cart.queryOrders({ orderIds: [orderId] });
    mutate('/action/cart/getCart');

    return (res.isError ? {} : res.data.items?.[0]) as Order;
  }, []);

  const getOrder = useCallback(async (orderId: string) => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.cart.getOrder({ orderId });
    mutate('/action/cart/getOrder');

    if (res.isError) {
      throw res.error;
    }

    return res.data as Order;
  }, []);

  const orderHistory = useCallback(async () => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.cart.queryOrders({ sortAttributes: [{ createdAt: 'desc' }] });

    return res.isError ? ([] as Order[]) : (res.data.items as Order[]);
  }, []);

  const getProjectSettings = useCallback(async () => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.project.getSettings();

    return res.isError ? {} : res.data;
  }, []);

  const removeItem = useCallback(async (lineItemId: string, customLineItemId?: string) => {
    const extensions = sdk.composableCommerce;

    const payload = {
      lineItem: { id: lineItemId, customLineItemId },
    };

    const res = await extensions.cart.removeItem(payload);
    /* A refusal (409: the finish cannot be attributed) is not a success: surface the server's
       message so the row can show it, and never publish or track it as a removal. */
    if (res.isError) throw new Error(res.error?.message || 'We could not remove that item. Your cart was not changed.');
    publishCart(res);
  }, []);

  const removeItems = useCallback(async (lineItems: { id: string; customLineItemId?: string }[]) => {
    const extensions = sdk.composableCommerce;

    const payload = {
      lineItems,
    };

    const res = await extensions.cart.removeItems(payload);
    if (res.isError) throw new Error(res.error?.message || 'We could not remove those items. Your cart was not changed.');
    publishCart(res);
  }, []);

  const removeDesign = useCallback(async (designId: string) => {
    const res = await sdk.composableCommerce.cart.removeDesign({ designId });
    if (res.isError) {
      throw new Error(res.error?.message || 'We could not remove that design. Your cart was not changed.');
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { removedLineItemIds, removedLineItems, ...cart } = res.data;
    publishCart({ ...res, data: cart } as typeof res);
    /* Pass through exactly what the server sent: an OLDER backend (a mixed-version deploy) sends no
       removedLineItems (or no ids either), and the caller falls back for analytics. */
    return {
      ...(Array.isArray(removedLineItems) ? { removedLineItems: removedLineItems as ExtendedLineItem[] } : {}),
      ...(Array.isArray(removedLineItemIds) ? { removedLineItemIds } : {}),
    };
  }, []);

  const updateItem = useCallback(
    async (lineItemId: string, customLineItemId: string | undefined, newQuantity: number) => {
      try {
        const extensions = sdk.composableCommerce;

        const payload: { lineItem: { id: string; customLineItemId?: string; count: number } } = {
          lineItem: {
            id: lineItemId,
            customLineItemId,
            count: newQuantity,
          },
        };

        const res = await extensions.cart.updateItem(payload);

        if (res.isError) {
          console.error('Error updating cart item:', res.error);
          return { isError: true, error: res.error };
        }

        publishCart(res);

        return { isError: false, data: res.data as Cart };
      } catch (error) {
        console.error('Unexpected error updating cart item:', error);
        return { isError: true, error };
      }
    },
    [],
  );

  const updateCart = useCallback(async (payload: CartDetails): Promise<Cart> => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.cart.updateCart(payload);

    publishCart(res);

    return (res.isError ? {} : res.data) as Cart;
  }, []);

  const setShippingMethod = useCallback(async (shippingMethodId: string) => {
    const extensions = sdk.composableCommerce;

    const payload = {
      shippingMethod: {
        id: shippingMethodId,
      },
    };

    const res = await extensions.cart.setShippingMethod(payload);

    publishCart(res);
  }, []);

  const redeemDiscountCode = useCallback(async (code: string) => {
    const extensions = sdk.composableCommerce;

    const payload = {
      code: code,
    };
    const res = await extensions.cart.redeemDiscountCode(payload);

    if (!res.isError && (res.data as Cart).cartId) {
      publishCart(res);
    } else {
      throw new Error((res.isError ? res.error.message : '') ?? 'code not valid');
    }
  }, []);

  const removeDiscountCode = useCallback(async (discount: Discount) => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.cart.removeDiscountCode({ discountId: discount.discountId as string });

    publishCart(res);
  }, []);

  const resetCart = useCallback(async () => {
    await sdk.callAction({ actionName: 'cart/resetCart' });
  }, []);

  const clearAllowedOrder = useCallback(async () => {
    await sdk.callAction({ actionName: 'cart/clearAllowedOrder' });
  }, []);

  const getExternalShippingMethods = async () => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.cart.getExternalShippingMethods();

    if (!res.isError && res.data) {
      return res.data;
    }

    throw new Error((res.isError ? res.error.message : '') ?? 'shipping methods not available');
  };

  const setExternalShippingMethod = useCallback(async (shippingCode: string, zipcode?: string) => {
    const extensions = sdk.composableCommerce;

    // The zipcode is the destination the rate was quoted for. Without it the server re-prices
    // against the cart's stored address, so the committed rate disagrees with the quoted one.
    const payload = { shippingCode, ...(zipcode ? { zipcode } : {}) };

    const res = await extensions.cart.setCustomShippingMethod(payload);

    if (res.isError) {
      throw new Error(res.error.message || 'Unable to apply the selected shipping method');
    }

    await publishCart(res);
    return res.data as Cart;
  }, []);

  // Throws rather than reporting failure through the return value. A swallowed tax failure is
  // indistinguishable from success, which is how a customer reaches payment on a cart whose tax
  // was never written — commercetools then refuses the order after Braintree has already charged.
  // Matches saveCart / loadSavedCart / sendSavedCartDetails below.
  const setExternalTaxAmount = useCallback(async () => {
    const extensions = sdk.composableCommerce;

    const res = await extensions.cart.setExternalTaxAmount();

    if (res.isError) throw new Error(res.error.message || 'Unable to calculate tax for your order');

    await publishCart(res);

    return res.data as Cart;
  }, []);

  const saveCart = useCallback(async () => {
    const res = await sdk.callAction<SavedCartResult>({ actionName: 'savedCart/save' });
    if (res.isError) throw new Error(res.error.message || 'Unable to save your cart');
    return res.data;
  }, []);

  const loadSavedCart = useCallback(async (code: string) => {
    const res = await sdk.callAction<Cart>({ actionName: 'savedCart/load', payload: { code } });
    if (res.isError) {
      await mutate('/action/cart/getCart');
      throw new Error(res.error.message || 'Unable to load that cart');
    }
    // Loading a saved cart is an explicit cart SWITCH: the one publish allowed to change cart id.
    publishCart(res, { switchesCart: true });
    return res.data;
  }, []);

  const sendSavedCartDetails = useCallback(async (payload: SavedCartDetails) => {
    const res = await sdk.callAction({ actionName: 'savedCart/sendDetails', payload: { ...payload } });
    if (res.isError) throw new Error(res.error.message || 'Unable to save your contact details');
  }, []);

  return {
    ...data,
    totalItems,
    isEmpty,
    isShippingAccurate,
    hasOutOfStockItems: false,
    transaction,
    addItem,
    addBuilderItems,
    addDPQItems,
    updateCart,
    setShippingMethod,
    removeItem,
    removeItems,
    removeDesign,
    updateItem,
    estimateShipping,
    orderCart,
    queryOrder,
    getOrder,
    orderHistory,
    getProjectSettings,
    redeemDiscountCode,
    removeDiscountCode,
    resetCart,
    clearAllowedOrder,
    billingAddress,
    shippingAddress,
    getExternalShippingMethods,
    setExternalShippingMethod,
    setExternalTaxAmount,
    saveCart,
    loadSavedCart,
    sendSavedCartDetails,
  };
};

export default useCart;
